import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Play, Pause, ArrowLeft, ArrowRight, X, ExternalLink, Volume2, Volume1, VolumeX, Mic,
} from 'lucide-react';

/*
 * Tour Player — auto-advancing screenshot sequence with professional voice narration,
 * captions, scrub bar, volume control, and click-through deep links into each feature.
 *
 * Voiceover uses the browser's Web Speech API (SpeechSynthesis), preferring the
 * highest-quality male English voice available on the user's platform.
 */

const SLIDES = [
  {
    img: '/tour/01-landing.jpg', label: 'Welcome', title: 'Close the books. Faster.',
    caption: 'SnuggleMind is the AI bookkeeping platform built for modern accounting firms — bookkeeping, close, reporting, and controls in one place.',
    narration: 'Welcome to SnuggleMind, the AI bookkeeping platform built for modern accounting firms. In the next ninety seconds, I will walk you through how the platform automates bookkeeping, accelerates month-end close, and gives your firm the controls partners actually trust.',
    deepLink: '/', deepLabel: 'View landing page',
  },
  {
    img: '/tour/02-solutions.jpg', label: 'Solutions', title: 'The bookkeeping brain your firm deserves',
    caption: 'Hybrid AI: GPT-5.2 reasoning + a deterministic rules engine + per-client policy context. Not a black box — a system you can explain to a partner.',
    narration: 'The Solutions page introduces our hybrid AI engine. We combine GPT-5.2 reasoning with a deterministic rules engine and a per-client context store. The result: every categorization decision is explainable, defensible, and aligned with your firm\'s policies — never a black box.',
    deepLink: '/solutions/bookkeeping', deepLabel: 'Explore solutions',
  },
  {
    img: '/tour/15-pricing.jpg', label: 'Pricing', title: 'Simple pricing. Seven-day free trial.',
    caption: 'Start free. No credit card. $29/user/month annual or $49 monthly. One plan, every feature.',
    narration: 'Pricing is simple. Start with a seven-day free trial — no credit card required. Twenty-nine dollars per user per month on the annual plan, forty-nine month-to-month. Every feature, every integration, every report — included from day one.',
    deepLink: '/pricing', deepLabel: 'See pricing',
  },
  {
    img: '/tour/04-dashboard.jpg', label: 'Dashboard', title: 'Your firm at a glance',
    caption: 'Action items, transaction volume, AI confidence distribution, classification breakdown, and learning insights — every dashboard surfaces the next decision, not just numbers.',
    narration: 'The Dashboard is your firm\'s command center. It surfaces action items, transaction volume, AI confidence distributions, and classification breakdowns. Each tile is designed to tell you what needs your attention next — not just what happened yesterday.',
    deepLink: '/dashboard', deepLabel: 'Open dashboard',
  },
  {
    img: '/tour/05-alerts.jpg', label: 'Alerts Center', title: 'Real-time anomaly detection',
    caption: 'One feed for exceptions, recon mismatches, missing docs, duplicate suspects, budget overruns, and unusual spending. Snooze, mark read, or take action inline.',
    narration: 'The Alerts Center is your real-time anomaly detection feed. It aggregates exceptions, reconciliation mismatches, missing documents, duplicate suspects, budget overruns, and unusual spending — all in one place. Snooze items for twenty-four hours, mark them read, or jump straight into the relevant workflow.',
    deepLink: '/alerts', deepLabel: 'Open alerts',
  },
  {
    img: '/tour/06-transactions.jpg', label: 'Transactions', title: 'Every transaction, fully explained',
    caption: 'AI insights show why a vendor matched, which policy applied, and what scenario was detected. Confidence scores and audit trails on every row.',
    narration: 'On the Transactions page, every row tells you why the AI made its decision. You see the matched vendor, the applied policy, the detected scenario, the confidence score, and the full audit trail. No more guessing how a category was assigned.',
    deepLink: '/transactions', deepLabel: 'View transactions',
  },
  {
    img: '/tour/07-exceptions.jpg', label: 'Exception Queue', title: 'Policy-aware exception triage',
    caption: 'Exceptions auto-classified into Capitalization, Owner-Related, Loan-Related, New Vendor, Missing Docs, and Unusual categories — clear next actions for every one.',
    narration: 'The Exception Queue automatically classifies every flagged transaction into clear categories: capitalization, owner-related, loan-related, new vendor, missing documents, or unusual activity. Each category has a recommended next action, so reviewers move three times faster.',
    deepLink: '/exception-queue', deepLabel: 'Open queue',
  },
  {
    img: '/tour/09-close-readiness.jpg', label: 'Close Readiness', title: 'Composite readiness score',
    caption: 'Live scoring across reconciliations, exceptions, missing docs, sensitive items, and policy flags. From "Not Ready" to "Ready for CPA" to "Closed" in days, not weeks.',
    narration: 'The Close Readiness Dashboard computes a live composite score across your reconciliations, exceptions, missing documents, sensitive items, and policy flags. You can see exactly what is blocking close, who owns it, and how close you are to a clean handoff.',
    deepLink: '/close-readiness', deepLabel: 'Check readiness',
  },
  {
    img: '/tour/08-recon.jpg', label: 'Recon Center', title: 'Reconcile in one workspace',
    caption: 'Bank, credit card, and clearing account reconciliations side-by-side. Upload statements, auto-match transactions, surface variance instantly.',
    narration: 'The Recon Center brings every reconciliation into a single workspace — bank accounts, credit cards, and clearing accounts side-by-side. Upload a statement, watch transactions auto-match, and resolve any variance in minutes instead of hours.',
    deepLink: '/recon-center', deepLabel: 'Open Recon Center',
  },
  {
    img: '/tour/11-knowledge-graph.jpg', label: 'Knowledge Graph', title: 'How the AI learns your firm',
    caption: 'Vendor-account relationships extracted from posted transactions. Smart Account Suggestions pull from this learning graph every time the AI categorizes.',
    narration: 'The Knowledge Graph shows how SnuggleMind learns your firm over time. Every posted transaction strengthens the vendor-to-account relationships, which then feed into Smart Account Suggestions for every new categorization. The longer you use it, the smarter it gets.',
    deepLink: '/knowledge-graph', deepLabel: 'See the graph',
  },
  {
    img: '/tour/13-import-wizard.jpg', label: 'Migration', title: 'Bring your books over from Botkeeper',
    caption: 'Botkeeper shut down Feb 7, 2026. Drop your last export — our 4-step wizard auto-detects columns and re-categorizes everything in minutes.',
    narration: 'Botkeeper shut down on February 7th, 2026. If your firm was affected, our four-step migration wizard absorbs your last export, auto-detects every column, and re-categorizes thousands of transactions in minutes — no manual mapping required.',
    deepLink: '/import/botkeeper', deepLabel: 'Start migration',
  },
  {
    img: '/tour/03-from-botkeeper.jpg', label: 'Migrate', title: 'Move your firm in 48 hours',
    caption: 'A 5-step playbook from Botkeeper sign-off to closing your first month. White-glove migration support included.',
    narration: 'And here is the playbook. A complete five-step path from Botkeeper sign-off to closing your first SnuggleMind month — usually within forty-eight hours. White-glove migration support is included for every firm switching over. Ready to begin? Click Start Free Trial below.',
    deepLink: '/from-botkeeper', deepLabel: 'View playbook',
  },
];

// Approximate fallback duration when speech synthesis is unavailable (4 wps reading rate)
const fallbackDuration = (text) => Math.max(6, Math.round(text.split(' ').length / 2.4));

export default function TourPlayerPage() {
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [volume, setVolume] = useState(0.8);
  const [progress, setProgress] = useState(0);
  const [voice, setVoice] = useState(null);
  const [voicesReady, setVoicesReady] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const tickRef = useRef(null);
  const startTimeRef = useRef(null);
  const speechDurationRef = useRef(null);
  // HTML5 Audio element — supports LIVE volume changes (the fix for slider restart)
  const audioRef = useRef(null);

  const current = SLIDES[idx];
  const audioOff = volume <= 0;
  // Use HTML5 Audio when the slide has a pre-rendered narration MP3, else fall back to Web Speech
  const hasAudioFile = !!current.audioFile;

  // ---------------- Voice selection ----------------
  // Strict priority: only natural/neural cloud voices. Avoid the robotic
  // legacy local voices (Microsoft David Desktop, eSpeak, etc.) entirely.
  const pickBestMaleVoice = useCallback(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return null;
    const voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) return null;

    // Tier 1 — Modern neural voices (sound genuinely human)
    const tier1 = [
      'Microsoft Guy Online (Natural) - English (United States)',
      'Microsoft Davis Online (Natural) - English (United States)',
      'Microsoft Brandon Online (Natural) - English (United States)',
      'Microsoft Tony Online (Natural) - English (United States)',
      'Microsoft Brian Online (Natural) - English (United Kingdom)',
      'Microsoft Ryan Online (Natural) - English (United Kingdom)',
      'Microsoft Thomas Online (Natural) - English (United Kingdom)',
      // Apple premium voices (macOS 14+ / iOS 17+) — much more natural than legacy
      'Tom (Premium)', 'Tom (Enhanced)',
      'Aaron (Premium)', 'Aaron (Enhanced)',
      'Reed (Premium)', 'Reed (Enhanced)',
      'Rocko (Premium)', 'Rocko (Enhanced)',
      'Arthur (Premium)', 'Arthur (Enhanced)',
      // Google Cloud voices (Chrome desktop)
      'Google UK English Male', 'Google US English Male',
    ];
    for (const target of tier1) {
      const v = voices.find((x) => x.name === target);
      if (v) return v;
    }

    // Tier 2 — partial match for "Online (Natural)" variants regardless of locale
    const naturalVoice = voices.find((v) =>
      /Online \(Natural\)/i.test(v.name) &&
      /^en/i.test(v.lang) &&
      /\b(guy|davis|brandon|tony|brian|ryan|thomas|christopher|eric|roger|steffan)\b/i.test(v.name)
    );
    if (naturalVoice) return naturalVoice;

    // Tier 3 — Apple "Premium" or "Enhanced" male voices
    const applePremium = voices.find((v) =>
      /(Premium|Enhanced)/i.test(v.name) &&
      /^en/i.test(v.lang) &&
      /\b(tom|aaron|reed|rocko|arthur|daniel|alex|fred)\b/i.test(v.name)
    );
    if (applePremium) return applePremium;

    // Tier 4 — non-local cloud voices (browser-flagged remote)
    const cloudMale = voices.find((v) =>
      v.localService === false &&
      /^en/i.test(v.lang) &&
      /\b(guy|davis|brandon|tony|brian|ryan|thomas|male|tom|aaron|reed|rocko|arthur|daniel|alex)\b/i.test(v.name)
    );
    if (cloudMale) return cloudMale;

    // Tier 5 — Daniel/Alex on older Apple (still better than Microsoft Desktop)
    const appleClassic = voices.find((v) => v.name === 'Daniel' || v.name === 'Alex');
    if (appleClassic) return appleClassic;

    // Tier 6 — Heuristic male match, but EXCLUDE known robotic voices
    const robotic = /(Desktop|eSpeak|Compact|Festival|Pico)/i;
    const heuristicMale = voices.find((v) =>
      /^en/i.test(v.lang) &&
      !robotic.test(v.name) &&
      /\b(male|man|guy|david|daniel|alex|fred|brian|matthew|james|john|tony|brandon|davis|reed|rocko|aaron|arthur|mark|paul|peter|ryan|tom|thomas)\b/i.test(v.name)
    );
    if (heuristicMale) return heuristicMale;

    // Last resort — any English voice that ISN'T flagged as robotic
    return voices.find((v) => /^en/i.test(v.lang) && !robotic.test(v.name)) ||
           voices.find((v) => /^en/i.test(v.lang)) ||
           voices[0];
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      setVoicesReady(true);
      return;
    }
    const update = () => {
      const v = pickBestMaleVoice();
      setVoice(v);
      setVoicesReady(true);
    };
    update();
    // Voices are async-loaded in some browsers
    window.speechSynthesis.onvoiceschanged = update;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, [pickBestMaleVoice]);

  // ---------------- Playback effect (HTML5 Audio OR Web Speech) ----------------
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Always cancel any prior playback first (both engines)
    if (window.speechSynthesis) window.speechSynthesis.cancel();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
    setSpeaking(false);

    if (!playing || audioOff) {
      speechDurationRef.current = fallbackDuration(current.narration);
      return;
    }

    // ---- Path A: HTML5 Audio (live volume support, no restart on slider) ----
    if (hasAudioFile) {
      const audio = new Audio(current.audioFile);
      audio.volume = volume;
      audio.preload = 'auto';
      audio.onplay = () => setSpeaking(true);
      audio.onended = () => {
        setSpeaking(false);
        setIdx((i) => (i + 1 >= SLIDES.length ? 0 : i + 1));
      };
      audio.onerror = () => {
        setSpeaking(false);
        // Graceful fallback to Web Speech if MP3 fails to load
        speakViaWebSpeech();
      };
      audio.onloadedmetadata = () => {
        speechDurationRef.current = audio.duration || fallbackDuration(current.narration);
      };
      audioRef.current = audio;
      audio.play().catch(() => speakViaWebSpeech());
      return () => {
        audio.pause();
        audio.onplay = audio.onended = audio.onerror = audio.onloadedmetadata = null;
      };
    }

    // ---- Path B: Web Speech fallback ----
    function speakViaWebSpeech() {
      if (!voicesReady || !window.speechSynthesis) return;
      const utter = new SpeechSynthesisUtterance(current.narration);
      if (voice) utter.voice = voice;
      utter.rate = 0.88;
      utter.pitch = 0.9;
      utter.volume = volume;
      utter.lang = voice?.lang || 'en-US';
      utter.onstart = () => setSpeaking(true);
      utter.onend = () => {
        setSpeaking(false);
        setIdx((i) => {
          if (window.speechSynthesis.speaking) return i;
          return i + 1 >= SLIDES.length ? 0 : i + 1;
        });
      };
      utter.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(utter);
      speechDurationRef.current = fallbackDuration(current.narration);
    }
    speakViaWebSpeech();

    return () => {
      if (window.speechSynthesis) window.speechSynthesis.cancel();
      setSpeaking(false);
    };
  }, [idx, playing, audioOff, voice, voicesReady, hasAudioFile]);

  // ---------------- LIVE volume control (HTML5 Audio supports this natively!) ----------------
  useEffect(() => {
    if (audioRef.current) {
      // This is the actual fix: HTML5 audio.volume updates instantly mid-playback
      audioRef.current.volume = volume;
    }
    // For Web Speech, we no longer cancel-and-respeak. The volume change applies
    // to the next utterance only (browser API limitation).
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      if (audioOff && window.speechSynthesis.speaking && !hasAudioFile) {
        window.speechSynthesis.cancel();
        setSpeaking(false);
      }
    }
  }, [volume, audioOff, hasAudioFile]);

  // ---------------- Visual progress ticker ----------------
  useEffect(() => {
    if (!playing) return;
    startTimeRef.current = Date.now();
    tickRef.current = setInterval(() => {
      const expected = speechDurationRef.current || fallbackDuration(current.narration);
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      setProgress(Math.min(elapsed / expected, 1));
    }, 100);
    return () => clearInterval(tickRef.current);
  }, [idx, playing, current.narration]);

  // Reset progress on slide change
  useEffect(() => { setProgress(0); }, [idx]);

  // Cleanup on unmount
  useEffect(() => () => {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
    }
  }, []);

  const totalDuration = useMemo(
    () => SLIDES.reduce((s, x) => s + fallbackDuration(x.narration), 0),
    []
  );
  const elapsedThroughIdx = useMemo(
    () => SLIDES.slice(0, idx).reduce((s, x) => s + fallbackDuration(x.narration), 0),
    [idx]
  );

  const next = useCallback(() => {
    setIdx((i) => (i + 1) % SLIDES.length);
  }, []);
  const prev = useCallback(() => {
    setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length);
  }, []);
  const togglePlay = useCallback(() => {
    setPlaying((p) => {
      if (typeof window !== 'undefined' && window.speechSynthesis) {
        if (p) window.speechSynthesis.cancel();
      }
      return !p;
    });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === ' ') { e.preventDefault(); togglePlay(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, togglePlay]);

  // Picks the right speaker icon based on level (purely visual)
  const VolumeIcon = useMemo(() => {
    if (volume <= 0) return VolumeX;
    if (volume <= 0.45) return Volume1;
    return Volume2;
  }, [volume]);

  const overallProgress = ((elapsedThroughIdx + (fallbackDuration(current.narration)) * progress) / totalDuration) * 100;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: '#0A1610', color: '#F7F6F2' }}
      data-testid="tour-page"
    >
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(247,246,242,0.08)' }}>
        <Link to="/" className="flex items-center gap-2.5" data-testid="tour-logo">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#D98A4A' }}>
            <span className="text-white font-heading text-[15px] font-bold">S</span>
          </div>
          <span className="font-heading text-[16px] font-semibold tracking-tight">SnuggleMind</span>
          <span className="ml-3 text-[10px] uppercase tracking-[0.22em] font-semibold px-2 py-1 rounded-full" style={{ background: 'rgba(217,138,74,0.18)', color: '#D98A4A' }}>
            Guided tour
          </span>
        </Link>

        <div className="flex items-center gap-2">
          {/* Volume control — slider only; icon is visual indicator */}
          <div
            className="hidden sm:flex items-center gap-2.5 pl-3 pr-3 h-9 rounded-full"
            style={{ background: 'rgba(247,246,242,0.05)', border: '1px solid rgba(247,246,242,0.08)' }}
            data-testid="volume-control"
          >
            <span
              className="inline-flex w-6 h-6 items-center justify-center"
              aria-hidden="true"
              data-testid="volume-icon"
            >
              <VolumeIcon size={15} style={{ color: volume <= 0 ? 'rgba(247,246,242,0.4)' : '#F7F6F2' }} />
            </span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="tour-volume-slider"
              style={{ width: '110px' }}
              aria-label="Narration volume"
              data-testid="volume-slider"
            />
            <span className="text-[10px] font-mono w-7 text-right tabular-nums" style={{ color: 'rgba(247,246,242,0.55)' }}>
              {Math.round(volume * 100)}
            </span>
          </div>

          <Link
            to="/"
            className="inline-flex w-9 h-9 rounded-full items-center justify-center transition-colors hover:bg-white/10"
            title="Exit tour"
            data-testid="exit-btn"
          >
            <X size={18} />
          </Link>
        </div>
      </header>

      {/* Stage */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 sm:px-8 py-6 lg:py-10">
        <div className="w-full max-w-[1200px]" data-testid="tour-stage">
          {/* Slide image */}
          <div
            className="relative rounded-2xl overflow-hidden mx-auto transition-all duration-500"
            style={{
              boxShadow: '0 30px 80px -20px rgba(0,0,0,0.5)',
              border: '1px solid rgba(247,246,242,0.1)',
              aspectRatio: '16 / 9',
              background: '#1A2A22',
            }}
          >
            <img
              key={current.img}
              src={current.img}
              alt={current.title}
              className="w-full h-full object-cover object-top animate-tour-fade"
              data-testid="tour-image"
            />

            {/* Slide-number badge */}
            <div
              className="absolute top-5 left-5 flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold"
              style={{ background: 'rgba(10,22,16,0.75)', backdropFilter: 'blur(8px)', color: '#D98A4A' }}
            >
              {speaking && (
                <span className="flex items-center gap-1.5" data-testid="narrating-indicator">
                  <Mic size={11} className="animate-pulse" style={{ color: '#D98A4A' }} />
                  <span className="text-[9px]">Narrating</span>
                  <span className="w-px h-3" style={{ background: 'rgba(217,138,74,0.4)' }} />
                </span>
              )}
              <span>{String(idx + 1).padStart(2, '0')} / {String(SLIDES.length).padStart(2, '0')} · {current.label}</span>
            </div>

            {/* Deep-link button (top-right) */}
            <Link
              to={current.deepLink}
              className="absolute top-5 right-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-body font-semibold transition-all hover:scale-[1.04]"
              style={{ background: '#D98A4A', color: '#FFFFFF' }}
              data-testid="deeplink-btn"
            >
              {current.deepLabel} <ExternalLink size={12} />
            </Link>
          </div>

          {/* Caption (always shown — mute controls audio only) */}
          <div className="max-w-3xl mx-auto mt-7 text-center px-3" data-testid="tour-caption">
            <h2
              key={`title-${idx}`}
              className="font-heading font-semibold tracking-[-0.02em] mb-3 animate-tour-fade"
              style={{ fontSize: 'clamp(1.25rem, 2.4vw, 1.85rem)', color: '#F7F6F2', lineHeight: 1.2 }}
            >
              {current.title}
            </h2>
            <p
              key={`cap-${idx}`}
              className="text-[14.5px] leading-relaxed animate-tour-fade"
              style={{ color: 'rgba(247,246,242,0.72)' }}
            >
              {current.caption}
            </p>
          </div>
        </div>
      </main>

      {/* Player controls */}
      <footer className="flex-shrink-0 px-4 sm:px-8 pb-6 pt-2">
        <div className="max-w-[1200px] mx-auto">
          {/* Scrub bar */}
          <div
            className="relative h-1 rounded-full mb-4 cursor-pointer group"
            style={{ background: 'rgba(247,246,242,0.12)' }}
            data-testid="scrub-bar"
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-100 ease-linear"
              style={{ width: `${overallProgress}%`, background: '#D98A4A' }}
            />
            {/* Slide tick marks */}
            {SLIDES.map((s, i) => {
              const tickPos = (SLIDES.slice(0, i).reduce((sum, x) => sum + x.duration, 0) / totalDuration) * 100;
              return (
                <button
                  key={s.label}
                  onClick={() => { setIdx(i); }}
                  className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full transition-transform group-hover:scale-150"
                  style={{
                    left: `${tickPos}%`,
                    background: i <= idx ? '#D98A4A' : 'rgba(247,246,242,0.3)',
                  }}
                  title={s.label}
                  data-testid={`scrub-tick-${i}`}
                  aria-label={`Jump to ${s.label}`}
                />
              );
            })}
          </div>

          {/* Buttons row */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <button
                onClick={prev}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
                aria-label="Previous"
                data-testid="prev-btn"
              >
                <ArrowLeft size={17} />
              </button>
              <button
                onClick={togglePlay}
                className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-105"
                style={{ background: '#D98A4A', color: '#FFFFFF' }}
                aria-label={playing ? 'Pause' : 'Play'}
                data-testid="play-toggle"
              >
                {playing ? <Pause size={18} fill="#FFFFFF" /> : <Play size={18} fill="#FFFFFF" className="ml-0.5" />}
              </button>
              <button
                onClick={next}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
                aria-label="Next"
                data-testid="next-btn"
              >
                <ArrowRight size={17} />
              </button>
            </div>

            <div className="hidden md:flex items-center gap-2 overflow-x-auto max-w-2xl">
              {SLIDES.map((s, i) => (
                <button
                  key={s.label}
                  onClick={() => setIdx(i)}
                  className="flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] font-body font-medium transition-all whitespace-nowrap"
                  style={{
                    background: i === idx ? '#D98A4A' : 'rgba(247,246,242,0.06)',
                    color: i === idx ? '#FFFFFF' : 'rgba(247,246,242,0.6)',
                  }}
                  data-testid={`chapter-${i}`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            <Link
              to="/register"
              className="hidden sm:inline-flex items-center gap-2 h-10 px-5 rounded-full text-[12.5px] font-body font-semibold whitespace-nowrap transition-all hover:scale-[1.02]"
              style={{ background: '#F7F6F2', color: '#0A1610' }}
              data-testid="tour-cta"
            >
              Start Free Trial <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
